/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
// ============================================================================
// USE ASYNC OPERATION - Hook for managing async operations with loading/error/success states
// ============================================================================

"use client";

import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { useErrorState } from "../state/use-error-state";

export interface UseAsyncOperationOptions<TData, TPayload> {
  /**
   * Async operation function to execute
   */
  operation: (payload: TPayload) => Promise<TData>;
  /**
   * Success message to display (optional)
   */
  successMessage?: string;
  /**
   * Error message prefix for toast notifications (optional)
   */
  errorMessagePrefix?: string;
  /**
   * Whether to show toast notifications (default: true)
   */
  showToast?: boolean;
  /**
   * Callback when operation succeeds
   */
  onSuccess?: (data: TData) => void;
  /**
   * Callback when operation fails
   */
  onError?: (error: string) => void;
  /**
   * Whether to clear error before operation (default: true)
   */
  clearErrorOnStart?: boolean;
}

export interface UseAsyncOperationReturn<TData, TPayload> {
  /**
   * Whether operation is in progress
   */
  isLoading: boolean;
  /**
   * Whether operation completed successfully
   */
  isSuccess: boolean;
  /**
   * Current error message, or null if no error
   */
  error: string | null;
  /**
   * Whether there is an error
   */
  hasError: boolean;
  /**
   * Last successful result data
   */
  data: TData | null;
  /**
   * Execute the async operation
   */
  execute: (payload: TPayload) => Promise<TData | null>;
  /**
   * Clear error state
   */
  clearError: () => void;
  /**
   * Reset all state (loading, error, success, data)
   */
  reset: () => void;
}

/**
 * Hook for managing async operations with loading, error, and success states
 *
 * Provides a consistent interface for async operations with automatic
 * error handling, toast notifications, and state management.
 *
 * @example
 * ```tsx
 * const { isLoading, error, execute, data } = useAsyncOperation({
 *   operation: async (id: string) => {
 *     const response = await api.get(`/api/items/${id}`);
 *     return response.data;
 *   },
 *   successMessage: "Item loaded successfully",
 *   errorMessagePrefix: "Failed to load item",
 *   onSuccess: (data) => {
 *     console.log("Success!", data);
 *   },
 * });
 *
 * const handleLoad = () => {
 *   execute(itemId);
 * };
 *
 * {isLoading && <Spinner />}
 * {error && <ErrorDisplay message={error} />}
 * {data && <ItemDisplay item={data} />}
 * ```
 */
export function useAsyncOperation<TData, TPayload = void>({
  operation,
  successMessage,
  errorMessagePrefix,
  showToast = true,
  onSuccess,
  onError,
  clearErrorOnStart = true,
}: UseAsyncOperationOptions<TData, TPayload>): UseAsyncOperationReturn<
  TData,
  TPayload
> {
  const { error, setError, clearError, hasError } = useErrorState();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [data, setData] = useState<TData | null>(null);

  // Store all caller-provided options in a ref so that `execute` is stable.
  // Without this, inline callbacks (onError, onSuccess) and operation functions
  // cause `execute` to be recreated every render, which breaks useEffect-driven
  // fetch loops (infinite re-render in React strict mode).
  const optionsRef = useRef({
    operation,
    successMessage,
    errorMessagePrefix,
    showToast,
    onSuccess,
    onError,
    clearErrorOnStart,
  });
  optionsRef.current = {
    operation,
    successMessage,
    errorMessagePrefix,
    showToast,
    onSuccess,
    onError,
    clearErrorOnStart,
  };

  const execute = useCallback(
    async (payload: TPayload): Promise<TData | null> => {
      const opts = optionsRef.current;
      if (opts.clearErrorOnStart) {
        clearError();
      }
      setIsLoading(true);
      setIsSuccess(false);

      try {
        const result = await opts.operation(payload);
        setData(result);
        setIsSuccess(true);

        if (opts.showToast && opts.successMessage) {
          toast.success(opts.successMessage, {
            description: `${opts.successMessage} successfully.`,
          });
        }

        opts.onSuccess?.(result);
        return result;
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : "An unexpected error occurred";

        setError(message);

        if (opts.showToast) {
          toast.error(opts.errorMessagePrefix ?? "Error", {
            description: message,
          });
        }

        opts.onError?.(message);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    // Only stable deps — caller-provided values read from optionsRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearError, setError],
  );

  const reset = useCallback(() => {
    clearError();
    setIsLoading(false);
    setIsSuccess(false);
    setData(null);
  }, [clearError]);

  return {
    isLoading,
    isSuccess,
    error,
    hasError,
    data,
    execute,
    clearError,
    reset,
  };
}
